import React, { ReactNode } from "react";
import {
  Routes,
  Route,
  BrowserRouter as Router,
  useLocation,
  Navigate,
} from "react-router";
import Login from "../pages/Login";
import Users from "../pages/UserDetails";
import Sidebar from "../components/Sidebar";
import Exercises from "../pages/Exercises";
import ProtectedRoute from "../components/ProtectedRoute";
import AuthService from "../services/authService";
import Equipments from "../pages/Equipments";
import Notifications from "../pages/Notifications";
import AddExercise from "../pages/AddExercise";
import EditExercise from "../pages/EditExercise";
import ExerciseDetails from "../pages/ExerciseDetails";
import AddEquipment from "../pages/AddEquipment";
import EditEquipment from "../pages/EditEquipment";
import AddNotification from "../pages/AddNotification";
import EditNotification from "../pages/EditNotification";
import Programs from "../pages/Programs";
import AddProgram from "../pages/AddProgram";
import EditProgram from "../pages/EditProgram";
import AddWeekWorkoutProgram from "../pages/AddWeekWorkout";
import WeekWorkoutsPage from "../pages/WeekWorkouts";
import TrainersList from "../pages/Trainers";
import AddTrainer from "../pages/AddTrainers";
import EditTrainer from "../pages/EditTrainer";
import EditWeekWorkoutProgram from "../pages/EditWeekWorkout";
import HowToPerformPage from "../pages/HTP";
import EditHowToPerform from "../pages/EditHTP";
import CreateHowToPerform from "../pages/AddHTP";
import TrainerProgramsPage from "../pages/TrainerPrograms";
import AddTrainerProgramPage from "../pages/AddTrainerProgram";
import EditTrainerProgramPage from "../pages/EditTrainerProgram";
import ExerciseSwapsPage from "../pages/ExerciseSwaps";
import ProfilePhotosList from "../pages/ProfilePhotos";
import MemberSuccessDashboard from "../pages/MemberSuccessDashboard";
import UserProfile from "../pages/UserProfile";

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const hideSidebarPaths = ["/login"];
  const isAuthenticated = AuthService.isAuthenticated();

  return (
    <div className="flex h-screen">
      {isAuthenticated && !hideSidebarPaths.includes(location.pathname) && (
        <Sidebar />
      )}

      <div className="flex-grow bg-gray-100 overflow-auto">{children}</div>
    </div>
  );
};

const AppNavigator: React.FC = () => {
  return (
    <Router>
      <Layout>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<MemberSuccessDashboard />} />
            <Route path="/users" element={<Users />} />
            <Route path="/users/:id" element={<UserProfile />} />
            <Route path="/exercises" element={<Exercises />} />
            <Route path="/add-exercise" element={<AddExercise />} />
            <Route path="/edit-exercise" element={<EditExercise />} />
            <Route path="/exercise-details" element={<ExerciseDetails />} />
            <Route path="/equipments" element={<Equipments />} />
            <Route path="/add-equipment" element={<AddEquipment />} />
            <Route path="/edit-equipment" element={<EditEquipment />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/add-notification" element={<AddNotification />} />
            <Route path="/edit-notification" element={<EditNotification />} />
            <Route path="/programs" element={<Programs />} />
            <Route path="/add-program" element={<AddProgram />} />
            <Route path="/edit-program" element={<EditProgram />} />
            <Route path="/week-program" element={<WeekWorkoutsPage />} />
            <Route
              path="/add-week-program"
              element={<AddWeekWorkoutProgram />}
            />
            <Route
              path="/edit-week-program"
              element={<EditWeekWorkoutProgram />}
            />
          </Route>
          <Route path="/trainers" element={<TrainersList />} />
          <Route path="/add-trainer" element={<AddTrainer />} />
          <Route path="/edit-trainer/:id" element={<EditTrainer />} />
          <Route path="/htp" element={<HowToPerformPage />} />
          <Route path="/edit-htp" element={<EditHowToPerform />} />
          <Route path="/add-htp" element={<CreateHowToPerform />} />
          <Route path="/trainer-programs" element={<TrainerProgramsPage />} />
          <Route path="/add-trainer-program" element={<AddTrainerProgramPage />} />
          <Route path="/edit-trainer-program" element={<EditTrainerProgramPage />} />
          <Route path="/exercise-swaps" element={<ExerciseSwapsPage />} />
          <Route path="/profile-photos" element={<ProfilePhotosList />} />
          <Route
            path="*"
            element={
              <Navigate
                to={AuthService.isAuthenticated() ? "/users" : "/login"}
                replace
              />
            }
          />
        </Routes>
      </Layout>
    </Router>
  );
};

export default AppNavigator;
